import React, { useState } from 'react';
import { User, Lock, ArrowRight, Mail, Phone, MapPin, Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
export function LoginRegister() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };
  return <div className="min-h-screen bg-gray-50 pt-40 lg:pt-52 pb-20" data-id="element-983">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-984">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-985">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-986">
            Student Portal
          </h1>
          <p className="text-lg text-gray-600" data-id="element-987">
            Access your on-demand classes or register to create an account
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3 max-w-2xl mx-auto" data-id="element-988">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-989" />
          <div data-id="element-990">
            <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-991">
              For On-Demand Classes Only
            </p>
            <p className="text-sm text-blue-800" data-id="element-992">
              This registration is for <strong data-id="element-993">On-Demand Classes only</strong>.
              If you're taking Zoom or In-Person classes, you do not need to
              register here.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8" data-id="element-994">
          <div className="inline-flex bg-white rounded-lg shadow-sm border border-gray-200 p-1" data-id="element-995">
            <button onClick={() => setActiveTab('login')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'login' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-996">
              Log In
            </button>
            <button onClick={() => setActiveTab('register')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'register' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-997">
              Register
            </button>
          </div>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && <Card className="max-w-md mx-auto p-8" data-id="element-998">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-999">
              Log In to Your Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1000">
              <div data-id="element-1001">
                <label htmlFor="login-username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1002">
                  Username
                </label>
                <div className="relative" data-id="element-1003">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1004">
                    <User className="h-5 w-5 text-gray-400" data-id="element-1005" />
                  </div>
                  <input type="text" id="login-username" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="Enter your username" required data-id="element-1006" />
                </div>
              </div>

              <div data-id="element-1007">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1008">
                  Password
                </label>
                <div className="relative" data-id="element-1009">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1010">
                    <Lock className="h-5 w-5 text-gray-400" data-id="element-1011" />
                  </div>
                  <input type="password" id="login-password" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="••••••••" required data-id="element-1012" />
                </div>
              </div>

              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1013">
                Log In <ArrowRight className="ml-2 h-4 w-4" data-id="element-1014" />
              </Button>
            </form>

            <div className="mt-6 text-center" data-id="element-1015">
              <a href="/14.html" className="text-sm text-gray-600 hover:text-brand-purple-600 hover:underline" data-id="element-1016">
                Forgot your password?
              </a>
            </div>
          </Card>}

        {/* Registration Form */}
        {activeTab === 'register' && <Card className="max-w-2xl mx-auto p-8" data-id="element-1017">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" data-id="element-1018">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center" data-id="element-1019">
              Register for on-demand classes
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1020">
              {/* Personal Information */}
              <div data-id="element-1021">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1022">
                  <User className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1023" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1024">
                  <div data-id="element-1025">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1026">
                      Title
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1027">
                      <option data-id="element-1028">Mr.</option>
                      <option data-id="element-1029">Ms.</option>
                      <option data-id="element-1030">Mrs.</option>
                      <option data-id="element-1031">Dr.</option>
                    </select>
                  </div>
                  <div data-id="element-1032">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1033">
                      First Name<span className="text-red-500" data-id="element-1034">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1035" />
                  </div>
                  <div data-id="element-1036">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1037">
                      Last Name<span className="text-red-500" data-id="element-1038">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1039" />
                  </div>
                  <div data-id="element-1040">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1041">
                      Company
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1042" />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div data-id="element-1043">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1044">
                  <MapPin className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1045" />
                  Billing Address
                </h3>
                <div className="space-y-4" data-id="element-1046">
                  <div data-id="element-1047">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1048">
                      Address 1<span className="text-red-500" data-id="element-1049">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1050" />
                  </div>
                  <div data-id="element-1051">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1052">
                      Address 2
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1053" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1054">
                    <div data-id="element-1055">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1056">
                        City<span className="text-red-500" data-id="element-1057">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1058" />
                    </div>
                    <div data-id="element-1059">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1060">
                        State/Province<span className="text-red-500" data-id="element-1061">*</span>
                      </label>
                      <select required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1062">
                        <option value="" data-id="element-1063">Select...</option>
                        <option value="AZ" data-id="element-1064">Arizona</option>
                        <option value="CA" data-id="element-1065">California</option>
                        <option value="NV" data-id="element-1066">Nevada</option>
                      </select>
                    </div>
                    <div data-id="element-1067">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1068">
                        Zip<span className="text-red-500" data-id="element-1069">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1070" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div data-id="element-1071">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1072">
                  <Phone className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1073" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1074">
                  <div data-id="element-1075">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1076">
                      Phone<span className="text-red-500" data-id="element-1077">*</span>
                    </label>
                    <input type="tel" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1078" />
                  </div>
                  <div data-id="element-1079">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1080">
                      Email<span className="text-red-500" data-id="element-1081">*</span>
                    </label>
                    <input type="email" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1082" />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div data-id="element-1083">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1084">
                  Additional Information
                </h3>
                <div className="space-y-4" data-id="element-1085">
                  <div data-id="element-1086">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1087">
                      How did you hear about us?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1088">
                      <option value="" data-id="element-1089">Select...</option>
                      <option data-id="element-1090">Google Search</option>
                      <option data-id="element-1091">Social Media</option>
                      <option data-id="element-1092">Referral</option>
                      <option data-id="element-1093">Other</option>
                    </select>
                  </div>
                  <div data-id="element-1094">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1095">
                      Are you on Facebook?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1096">
                      <option value="" data-id="element-1097">Select...</option>
                      <option data-id="element-1098">Yes</option>
                      <option data-id="element-1099">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Username & Password */}
              <div data-id="element-1100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1101">
                  <Lock className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1102" />
                  Create Your Username & Password
                </h3>
                <div className="space-y-4" data-id="element-1103">
                  <div data-id="element-1104">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1105">
                      Username<span className="text-red-500" data-id="element-1106">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1107" />
                  </div>
                  <div data-id="element-1108">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1109">
                      Password<span className="text-red-500" data-id="element-1110">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1111" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1112">
                      Must be at least 8 characters
                    </p>
                  </div>
                  <div data-id="element-1113">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1114">
                      Confirm Password<span className="text-red-500" data-id="element-1115">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1116" />
                  </div>
                </div>
              </div>

              {/* License Expiration Date */}
              <div data-id="element-1117">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1118">
                  <Calendar className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1119" />
                  License Expiration Date
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1120">
                  <div data-id="element-1121">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1122">
                      Month
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1123">
                      <option data-id="element-1124">January</option>
                      <option data-id="element-1125">February</option>
                      <option data-id="element-1126">March</option>
                      <option data-id="element-1127">April</option>
                      <option data-id="element-1128">May</option>
                      <option data-id="element-1129">June</option>
                      <option data-id="element-1130">July</option>
                      <option data-id="element-1131">August</option>
                      <option data-id="element-1132">September</option>
                      <option data-id="element-1133">October</option>
                      <option data-id="element-1134">November</option>
                      <option data-id="element-1135">December</option>
                    </select>
                  </div>
                  <div data-id="element-1136">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1137">
                      Year
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1138">
                      <option data-id="element-1139">2024</option>
                      <option data-id="element-1140">2025</option>
                      <option data-id="element-1141">2026</option>
                      <option data-id="element-1142">2027</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2" data-id="element-1143">
                  <a href="#" className="hover:underline" data-id="element-1144">
                    Click here to obtain your license expiration date from ADRE
                  </a>
                </p>
              </div>

              {/* Identity Confirmation */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1145">
                <div className="flex items-start" data-id="element-1146">
                  <input type="checkbox" id="identity-confirm" required className="mt-1 h-4 w-4 text-brand-purple-600 focus:ring-brand-purple-500 border-gray-300 rounded" data-id="element-1147" />
                  <label htmlFor="identity-confirm" className="ml-3 text-sm text-gray-700" data-id="element-1148">
                    <strong data-id="element-1149">I affirm this information to be true.</strong> The
                    Arizona Department of Real Estate requires that we confirm
                    your information and that you are the person taking the
                    classes. Incorporating a real person's name and license
                    number into a registration without the permission of both
                    parties, if you are NOT the person named in the
                    registration, we strongly recommend that you log out and not
                    continue. We reserve the right to use reasonable means to
                    verify your identity.
                  </label>
                </div>
              </div>

              {/* Privacy Statement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1150">
                <h4 className="font-semibold text-blue-900 mb-2" data-id="element-1151">
                  Privacy Statement
                </h4>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1152">
                  By registering with Desert Sage Seminars, you will create an
                  account by which we can administer your real estate license
                  renewal classes. Your information will be kept confidential
                  and not shared with any other party.
                </p>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1153">
                  The Arizona Department of Real Estate requires that we confirm
                  your information and that you are the person taking the
                  classes.
                </p>
                <p className="text-sm text-blue-800" data-id="element-1154">
                  Payments for our classes are made through a 3rd party merchant
                  account secured by our site, and they do not share your credit
                  card information with us or any other party.
                </p>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1155">
                Create Account <CheckCircle2 className="ml-2 h-5 w-5" data-id="element-1156" />
              </Button>
            </form>
          </Card>}
      </div>
    </div>;
}